import AnimatedCounter from '@components/common/AnimatedCounter';
import Icon from '@components/common/Icon';

const stats = [
  {
    id: 1,
    value: '15+',
    label: 'Academic Honors',
    description: 'Including national and international recognition',
    icon: 'academics',
    color: 'var(--color-accent-6)'
  },
  {
    id: 2,
    value: '3',
    label: 'Honor Societies',
    description: 'Memberships in prestigious academic organizations',
    icon: 'academics',
    color: 'var(--color-accent-6)'
  },
  {
    id: 3,
    value: '4',
    label: 'Mathematics Awards',
    description: 'Specialized recognition in mathematics excellence',
    icon: 'research',
    color: 'var(--color-accent-6)'
  },
  {
    id: 4,
    value: '4',
    label: 'Years of Excellence',
    description: 'Consistent honor roll from Grade 9 to present',
    icon: 'academics',
    color: 'var(--color-accent-6)'
  }
];

const AcademicsStatsSection = () => {
  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-theme-1)' }}>
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2
            className="text-4xl sm:text-5xl font-heading font-bold mb-4"
            style={{ color: 'var(--color-heading)' }}
          >
            Academic Impact at a Glance
          </h2>
          <p
            className="text-lg sm:text-xl max-w-3xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            Quantifying a commitment to academic excellence and scholarly achievement
          </p>
        </div>

        {/* Stats Grid - Horizontal scroll on mobile, grid on desktop */}
        <div className="sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 lg:gap-8 overflow-x-auto sm:overflow-x-visible">
          <div className="flex sm:contents gap-6 pb-4 sm:pb-0">
          {stats.map((stat) => (
            <div
              key={stat.id}
              className="group relative rounded-xl p-8 shadow-card transition-all duration-300 hover:shadow-card-hover flex-shrink-0 w-[280px] sm:w-auto"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderColor: 'var(--color-card-border)',
                borderWidth: '1px',
                borderStyle: 'solid'
              }}
            >
              {/* Decorative Corner */}
              <div
                className="absolute top-0 right-0 w-20 h-20 rounded-bl-full opacity-10"
                style={{ backgroundColor: stat.color }}
              ></div>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div
                  className="inline-flex items-center justify-center w-14 h-14 rounded-xl transition-transform duration-300 group-hover:scale-110"
                  style={{
                    backgroundColor: 'var(--color-accent-1)',
                    color: stat.color
                  }}
                >
                  <Icon name={stat.icon} size="xl" />
                </div>
              </div>

              {/* Stat Value */}
              <div className="text-center mb-3">
                <div
                  className="text-5xl font-heading font-extrabold mb-2"
                  style={{ color: stat.color }}
                >
                  <AnimatedCounter value={stat.value} duration={2000} />
                </div>
                <h3
                  className="text-xl font-bold"
                  style={{ color: 'var(--color-heading)' }}
                >
                  {stat.label}
                </h3>
              </div>

              {/* Description */}
              <p
                className="text-sm text-center leading-relaxed"
                style={{ color: 'var(--color-subtle)' }}
              >
                {stat.description}
              </p>

              {/* Hover Line */}
              <div
                className="absolute bottom-0 left-0 right-0 h-1 rounded-b-xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                style={{ backgroundColor: stat.color }}
              ></div>
            </div>
          ))}
          </div>
        </div>

        {/* Additional Context */}
        <div className="mt-12 max-w-5xl mx-auto">
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-6 p-8 rounded-xl"
            style={{
              backgroundColor: 'var(--color-accent-1)',
              borderColor: 'var(--color-accent-5)',
              borderWidth: '1px',
              borderStyle: 'solid'
            }}
          >
            <div className="text-center">
              <Icon
                name="academics"
                size="xl"
                className="mb-3"
                style={{ color: 'var(--color-accent-6)' }}
              />
              <h4
                className="text-lg font-bold mb-2"
                style={{ color: 'var(--color-heading)' }}
              >
                AP Scholar
              </h4>
              <p
                className="text-sm"
                style={{ color: 'var(--color-subtle)' }}
              >
                Distinction & Honors in Advanced Placement exams
              </p>
            </div>

            <div className="text-center">
              <Icon
                name="academics"
                size="xl"
                className="mb-3"
                style={{ color: 'var(--color-accent-6)' }}
              />
              <h4
                className="text-lg font-bold mb-2"
                style={{ color: 'var(--color-heading)' }}
              >
                International Scholar
              </h4>
              <p
                className="text-sm"
                style={{ color: 'var(--color-subtle)' }}
              >
                IASAS Scholar Award for regional excellence
              </p>
            </div>

            <div className="text-center">
              <Icon
                name="research"
                size="xl"
                className="mb-3"
                style={{ color: 'var(--color-accent-6)' }}
              />
              <h4
                className="text-lg font-bold mb-2"
                style={{ color: 'var(--color-heading)' }}
              >
                Math Excellence
              </h4>
              <p
                className="text-sm"
                style={{ color: 'var(--color-subtle)' }}
              >
                Multiple mathematics awards and honors society
              </p>
            </div>
          </div>
        </div>

        {/* Academic Philosophy */}
        <div className="mt-12 max-w-4xl mx-auto text-center">
          <div
            className="p-8 rounded-xl shadow-card"
            style={{
              backgroundColor: 'var(--color-card-bg)',
              borderColor: 'var(--color-card-border)',
              borderWidth: '1px',
              borderStyle: 'solid'
            }}
          >
            <h3
              className="text-2xl font-bold mb-4"
              style={{ color: 'var(--color-heading)' }}
            >
              Commitment to Academic Excellence
            </h3>
            <p
              className="text-lg leading-relaxed"
              style={{ color: 'var(--color-subtle)' }}
            >
              These achievements represent more than academic performance—they reflect a dedication to
              intellectual curiosity, rigorous scholarship, and a passion for learning that extends beyond
              the classroom. Each honor recognizes not only academic accomplishment but also the character,
              leadership, and service that define meaningful education.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AcademicsStatsSection;
